/**
 * Career Preferences Section Component
 * In-depth section for candidates to specify their career preferences
 * Includes fill-in-the-blanks for basics and short answer for detail
 */

import React, { useState, useEffect } from 'react';
import { Target, PenSquare, X, Check, Plus, X as XIcon } from 'lucide-react';
import { CandidateProfile } from '../../services/candidateServiceApi';
import { useDirtyState } from '../../hooks/useDirtyState';

interface CareerPreferences {
    locations: string[];
    minCompensation: string;
    maxCompensation: string;
    workType: string[];
    jobTitles: string[];
    startDateAvailability: string;
    lookingFor: string;
}

interface CareerPreferencesSectionProps {
    profile: CandidateProfile;
    onSave?: (preferences: CareerPreferences) => Promise<void>;
}

const WORK_TYPE_OPTIONS = ['Full-time', 'Part-time', 'Internship', 'Contract'];
const START_DATE_OPTIONS = [
    'Immediately',
    'Within 2 weeks',
    'Within 1 month',
    'Within 3 months',
    'Flexible'
];

const CareerPreferencesSection: React.FC<CareerPreferencesSectionProps> = ({ profile, onSave }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [newLocation, setNewLocation] = useState('');
    const [newJobTitle, setNewJobTitle] = useState('');

    // Get preferences from profile
    const existingPreferences = profile?.preferences || {};

    const [preferences, setPreferences] = useState<CareerPreferences>({
        locations: existingPreferences.cities || [],
        minCompensation: existingPreferences.minSalary?.toString() || '',
        maxCompensation: existingPreferences.maxSalary?.toString() || '',
        workType: existingPreferences.workType ? [existingPreferences.workType] : [],
        jobTitles: existingPreferences.jobTitles || [],
        startDateAvailability: existingPreferences.startDate || '',
        lookingFor: existingPreferences.lookingFor || ''
    });

    // Track dirty state for save button
    const { isDirty, setOriginal, reset: resetDirty } = useDirtyState(preferences);

    // Reinitialize when entering edit mode
    useEffect(() => {
        if (isEditing) {
            const initialPreferences = {
                locations: existingPreferences.cities || [],
                minCompensation: existingPreferences.minSalary?.toString() || '',
                maxCompensation: existingPreferences.maxSalary?.toString() || '',
                workType: existingPreferences.workType ? [existingPreferences.workType] : [],
                jobTitles: existingPreferences.jobTitles || [],
                startDateAvailability: existingPreferences.startDate || '',
                lookingFor: existingPreferences.lookingFor || ''
            };
            setPreferences(initialPreferences);
            setOriginal(initialPreferences);
        } else {
            resetDirty();
        }
    }, [isEditing]);

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setNewLocation('');
        setNewJobTitle('');
    };

    const handleSave = async () => {
        if (onSave) {
            setIsSaving(true);
            try {
                await onSave(preferences);
                setIsEditing(false);
            } catch (error) {
                console.error('Failed to save preferences:', error);
            } finally {
                setIsSaving(false);
            }
        } else {
            setIsEditing(false);
        }
    };

    const addLocation = () => {
        if (newLocation.trim() && !preferences.locations.includes(newLocation.trim())) {
            setPreferences({
                ...preferences,
                locations: [...preferences.locations, newLocation.trim()]
            });
            setNewLocation('');
        }
    };

    const removeLocation = (location: string) => {
        setPreferences({
            ...preferences,
            locations: preferences.locations.filter(l => l !== location)
        });
    };

    const addJobTitle = () => {
        if (newJobTitle.trim() && !preferences.jobTitles.includes(newJobTitle.trim())) {
            setPreferences({
                ...preferences,
                jobTitles: [...preferences.jobTitles, newJobTitle.trim()]
            });
            setNewJobTitle('');
        }
    };

    const removeJobTitle = (title: string) => {
        setPreferences({
            ...preferences,
            jobTitles: preferences.jobTitles.filter(t => t !== title)
        });
    };

    const toggleWorkType = (type: string) => {
        const current = preferences.workType;
        if (current.includes(type)) {
            setPreferences({
                ...preferences,
                workType: current.filter(t => t !== type)
            });
        } else {
            setPreferences({
                ...preferences,
                workType: [...current, type]
            });
        }
    };

    // Check if preferences are empty
    const isEmpty =
        preferences.locations.length === 0 &&
        preferences.jobTitles.length === 0 &&
        preferences.workType.length === 0 &&
        !preferences.minCompensation &&
        !preferences.maxCompensation &&
        !preferences.startDateAvailability &&
        !preferences.lookingFor;

    // Display view (non-editing mode)
    if (!isEditing) {
        return (
            <div className="bg-white rounded-2xl border border-[#EDEDED] p-6 w-full font-manrope">
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-2">
                        <Target size={20} className="text-[#0B111E]" />
                        <h3 className="text-lg font-semibold text-[#0B111E]">Career Preferences</h3>
                    </div>
                    <button
                        onClick={handleEdit}
                        className="text-[#015FA6] hover:bg-blue-50 p-2 rounded-full transition-colors"
                    >
                        <PenSquare size={20} />
                    </button>
                </div>

                {isEmpty ? (
                    <div className="text-center py-6">
                        <p className="text-sm text-gray-500 mb-4">Tell us what you're looking for in your next role</p>
                        <button
                            onClick={handleEdit}
                            className="px-4 py-2 bg-[#015FA6] text-white rounded-lg text-sm hover:bg-[#014a85] transition-colors"
                        >
                            Add Preferences
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {preferences.jobTitles.length > 0 && (
                            <div>
                                <h5 className="text-xs font-medium text-gray-500 mb-2">Target Roles</h5>
                                <div className="flex flex-wrap gap-2">
                                    {preferences.jobTitles.map((title, i) => (
                                        <span key={i} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                                            {title}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {preferences.locations.length > 0 && (
                            <div>
                                <h5 className="text-xs font-medium text-gray-500 mb-2">Preferred Locations</h5>
                                <div className="flex flex-wrap gap-2">
                                    {preferences.locations.map((loc, i) => (
                                        <span key={i} className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
                                            {loc}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {preferences.workType.length > 0 && (
                            <div>
                                <h5 className="text-xs font-medium text-gray-500 mb-2">Work Type</h5>
                                <div className="flex flex-wrap gap-2">
                                    {preferences.workType.map((type, i) => (
                                        <span key={i} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                                            {type}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {(preferences.minCompensation || preferences.maxCompensation) && (
                            <div>
                                <h5 className="text-xs font-medium text-gray-500 mb-2">Target Compensation</h5>
                                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                                    {preferences.minCompensation && preferences.maxCompensation
                                        ? `$${parseInt(preferences.minCompensation).toLocaleString()} - $${parseInt(preferences.maxCompensation).toLocaleString()}`
                                        : preferences.minCompensation
                                            ? `$${parseInt(preferences.minCompensation).toLocaleString()}+`
                                            : `Up to $${parseInt(preferences.maxCompensation).toLocaleString()}`
                                    }
                                </span>
                            </div>
                        )}

                        {preferences.startDateAvailability && (
                            <div>
                                <h5 className="text-xs font-medium text-gray-500 mb-2">Availability</h5>
                                <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                                    {preferences.startDateAvailability}
                                </span>
                            </div>
                        )}

                        {preferences.lookingFor && (
                            <div>
                                <h5 className="text-xs font-medium text-gray-500 mb-2">What are you looking for?</h5>
                                <p className="text-sm text-[#515151] bg-gray-50 p-3 rounded-lg">
                                    {preferences.lookingFor}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // Edit mode
    return (
        <div className="bg-white rounded-2xl border border-[#EDEDED] p-6 w-full font-manrope">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-2">
                    <Target size={20} className="text-[#0B111E]" />
                    <h3 className="text-lg font-semibold text-[#0B111E]">Career Preferences</h3>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleCancel}
                        className="text-gray-500 hover:bg-gray-100 p-2 rounded-full transition-colors"
                        disabled={isSaving}
                    >
                        <X size={20} />
                    </button>
                    <button
                        onClick={handleSave}
                        className={`p-2 rounded-full transition-colors ${
                            isDirty ? 'text-green-600 hover:bg-green-50' : 'text-gray-300 cursor-not-allowed'
                        }`}
                        disabled={isSaving || !isDirty}
                    >
                        <Check size={20} />
                    </button>
                </div>
            </div>

            <div className="space-y-5">
                {/* Target Roles */}
                <div>
                    <label className="block text-sm font-medium text-[#0B111E] mb-2">Target Roles</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                        {preferences.jobTitles.map((title, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                                {title}
                                <button onClick={() => removeJobTitle(title)} className="hover:text-blue-600">
                                    <XIcon size={14} />
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={newJobTitle}
                            onChange={(e) => setNewJobTitle(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addJobTitle()}
                            placeholder="e.g. Software Engineer, Product Manager"
                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                        />
                        <button
                            onClick={addJobTitle}
                            className="px-3 py-2 bg-[#015FA6] text-white rounded-lg hover:bg-[#014a85] transition-colors"
                        >
                            <Plus size={18} />
                        </button>
                    </div>
                </div>

                {/* Preferred Locations */}
                <div>
                    <label className="block text-sm font-medium text-[#0B111E] mb-2">Preferred Locations</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                        {preferences.locations.map((loc, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
                                {loc}
                                <button onClick={() => removeLocation(loc)} className="hover:text-orange-600">
                                    <XIcon size={14} />
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={newLocation}
                            onChange={(e) => setNewLocation(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addLocation()}
                            placeholder="e.g. San Francisco, New York, Remote"
                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                        />
                        <button
                            onClick={addLocation}
                            className="px-3 py-2 bg-[#015FA6] text-white rounded-lg hover:bg-[#014a85] transition-colors"
                        >
                            <Plus size={18} />
                        </button>
                    </div>
                </div>

                {/* Work Type */}
                <div>
                    <label className="block text-sm font-medium text-[#0B111E] mb-2">Work Type</label>
                    <div className="flex flex-wrap gap-2">
                        {WORK_TYPE_OPTIONS.map((type) => (
                            <button
                                key={type}
                                onClick={() => toggleWorkType(type)}
                                className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
                                    preferences.workType.includes(type)
                                        ? 'bg-green-100 text-green-800 border-green-300'
                                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                {type}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Target Compensation */}
                <div>
                    <label className="block text-sm font-medium text-[#0B111E] mb-2">Target Compensation (Annual)</label>
                    <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <label className="block text-xs text-gray-500 mb-1">Minimum</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                                <input
                                    type="number"
                                    value={preferences.minCompensation}
                                    onChange={(e) => setPreferences({ ...preferences, minCompensation: e.target.value })}
                                    placeholder="80,000"
                                    className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                />
                            </div>
                        </div>
                        <span className="text-gray-400 pt-5">—</span>
                        <div className="flex-1">
                            <label className="block text-xs text-gray-500 mb-1">Maximum</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                                <input
                                    type="number"
                                    value={preferences.maxCompensation}
                                    onChange={(e) => setPreferences({ ...preferences, maxCompensation: e.target.value })}
                                    placeholder="150,000"
                                    className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Start Date Availability */}
                <div>
                    <label className="block text-sm font-medium text-[#0B111E] mb-2">When can you start?</label>
                    <select
                        value={preferences.startDateAvailability}
                        onChange={(e) => setPreferences({ ...preferences, startDateAvailability: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6] bg-white"
                    >
                        <option value="">Select availability</option>
                        {START_DATE_OPTIONS.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>

                {/* What are you looking for? (Short answer) */}
                <div>
                    <label className="block text-sm font-medium text-[#0B111E] mb-2">What are you looking for?</label>
                    <textarea
                        value={preferences.lookingFor}
                        onChange={(e) => setPreferences({ ...preferences, lookingFor: e.target.value })}
                        placeholder="Describe your ideal role, company culture, growth opportunities, team dynamics, or any other factors important to you..."
                        rows={4}
                        maxLength={1000}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6] resize-none"
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">
                        {preferences.lookingFor.length}/1000 characters
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CareerPreferencesSection;
